import { useState, type FormEvent } from "react";
import { tEnglish as t } from "@buza/i18n";
import { api } from "../api.js";

// S-008 set-password page, opened from the NS-03 link (https://app.{domain}/#/set-password/{token})
// for a new staff user (S04) and for a reset (S01). The token comes from the fragment, which the
// app removes from the address bar before this renders (linkTokenFromHash), and is sent only in
// the request body. Screens are paused (4 Nov 2026): minimal, accessible, phrase keys only; it
// moves onto the ZD shell later.

const PASSWORD_MIN = 10;
const PASSWORD_MAX = 200;
const LINK = /^#\/set-password\/([A-Za-z0-9_-]{43})$/;

/** The token of a set-password link in a location hash, or undefined. */
export function linkTokenFromHash(hash: string): string | undefined {
  return LINK.exec(hash)?.[1];
}

export function SetPassword({ token, onDone }: { token: string | undefined; onDone: () => void }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(token ? undefined : t("parlour.reset.link_missing"));
  const [fields, setFields] = useState<Record<string, string>>({});
  const [done, setDone] = useState<string | undefined>();

  async function submit(e: FormEvent) {
    e.preventDefault();
    const local: Record<string, string> = {};
    if (password.length < PASSWORD_MIN || password.length > PASSWORD_MAX) local.password = t("parlour.signin.password_rule");
    else if (confirm !== password) local.confirm = t("parlour.reset.mismatch");
    setFields(local);
    if (Object.keys(local).length || !token) return;
    setBusy(true);
    const r = await api<{ message: string }>("/v1/parlour/password/set", { method: "POST", body: { token, password } });
    setBusy(false);
    setPassword("");
    setConfirm("");
    if (r.ok) {
      setError(undefined);
      setDone(r.data.message);
      return;
    }
    setFields(r.error?.fields ?? {});
    setError(r.status === 0 ? t("common.errors.no_connection") : r.error?.code === "VALIDATION" ? undefined : (r.error?.message ?? t("common.errors.validation")));
  }

  return (
    <main className="s01">
      <h1>{t("parlour.shell.app_name")}</h1>
      <h2>{t("parlour.reset.set_title")}</h2>
      {done ? (
        <p role="status">{done}</p>
      ) : (
        <>
          <p>{t("parlour.reset.set_intro")}</p>
          {error && (
            <p role="alert" className="error">
              {error}
            </p>
          )}
          {token && (
            <form onSubmit={submit} noValidate aria-label={t("parlour.reset.set_title")}>
              <label>
                {t("parlour.reset.password_label")}
                <input
                  type="password"
                  name="new-password"
                  autoComplete="new-password"
                  required
                  minLength={PASSWORD_MIN}
                  maxLength={PASSWORD_MAX}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  aria-invalid={!!fields.password}
                  aria-describedby="set-password-hint"
                />
              </label>
              <p id="set-password-hint" className="hint">
                {t("parlour.signin.password_hint")}
              </p>
              {fields.password && <p className="field-error">{fields.password}</p>}
              <label>
                {t("parlour.reset.confirm_label")}
                <input
                  type="password"
                  name="confirm-password"
                  autoComplete="new-password"
                  required
                  maxLength={PASSWORD_MAX}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  aria-invalid={!!fields.confirm}
                />
              </label>
              {fields.confirm && <p className="field-error">{fields.confirm}</p>}
              <button type="submit" disabled={busy}>
                {busy ? t("parlour.reset.set_working") : t("parlour.reset.set_submit")}
              </button>
            </form>
          )}
        </>
      )}
      <p>
        <button type="button" onClick={onDone}>
          {t("parlour.reset.back")}
        </button>
      </p>
    </main>
  );
}
